import React, { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { BookOpen, Scale, TrendingUp, Wallet, Percent, Target, ArrowDownRight, ArrowRight, FileDown, FileText, FileSpreadsheet, Package, Coins, Trash2, Banknote } from "lucide-react";
import { C } from "./AccountingLayout";
import { downloadReport } from "./exportHelpers";

const REPORTS = [
  { key: "general-ledger",  label: "General Ledger",  desc: "Every posted journal — drillable to source.",     Icon: BookOpen,        color: C.burgundy, tint: "#FBEBEB", exportable: true,  csv: true, pdf: true, to: "/accounting/general-ledger" },
  { key: "trial-balance",   label: "Trial Balance",   desc: "Debits = Credits balance verifier.",              Icon: Scale,           color: "#00897B",  tint: "#DBEEEB", exportable: true,  csv: true, pdf: true, to: null },
  { key: "profit-loss",     label: "Profit & Loss",   desc: "Revenue · COGS · Gross Profit · EBITDA · Net.",    Icon: TrendingUp,      color: C.green,    tint: C.greenTint, exportable: true, csv: true, pdf: true, to: null },
  { key: "aged-payables",   label: "Aged Payables",   desc: "0-30 / 31-60 / 61-90 / 90+ buckets.",             Icon: ArrowDownRight,  color: "#B45309",  tint: "#FEF3D8", exportable: true,  csv: true, pdf: true, to: "/accounting/accounts-payable" },
  { key: "vat-summary",     label: "VAT Report",      desc: "Taxable sales, collected & payable.",             Icon: Percent,         color: "#D63384",  tint: "#FCE4EF", exportable: true,  csv: true, pdf: true, to: "/accounting/tax-vat" },
  { key: "budget-variance", label: "Budget vs Actual", desc: "Variance by account, this period.",              Icon: Target,          color: "#6E28A6",  tint: "#F2E7FD", exportable: false, csv: false, pdf: false, to: "/accounting/budget-actual" },
  { key: "cash-flow",       label: "Cash Flow",       desc: "Inflow / outflow across cash accounts.",          Icon: Wallet,          color: C.gold,     tint: "#FEF3D8", exportable: false, csv: false, pdf: false, to: "/accounting/bank-cash" },
];

// Workbook-format reports (daily Excel templates) → live in the Reports Hub / Financial Statements
const WORKBOOKS = [
  { hub: "revenue",       label: "Revenue & Collections", desc: "Daily category sales, payments & variance (per-day sheets).", Icon: TrendingUp, color: C.burgundy, tint: "#FBEBEB", to: "/accounting/reports-hub" },
  { hub: "expenses",      label: "Departmental Expenses", desc: "Daily departmental expense analysis (items × days).",        Icon: Coins,      color: "#B45309",  tint: "#FEF3D8", to: "/accounting/reports-hub" },
  { hub: "petty-cash",    label: "Petty Cash Register",   desc: "PVC voucher register with running balance.",                 Icon: Wallet,     color: C.gold,     tint: "#FEF3D8", to: "/accounting/reports-hub" },
  { hub: "bad-damaged",   label: "Bad & Damaged (B&D)",   desc: "Daily wastage grid per branch + loss-to-sales.",             Icon: Trash2,     color: "#D63384",  tint: "#FCE4EF", to: "/accounting/reports-hub" },
  { hub: "inventory",     label: "Stock Inventory",       desc: "Daily inventory by stock group (General/Drinks/Gelato/Food/Cake).", Icon: Package, color: "#00897B", tint: "#DBEEEB", to: "/accounting/reports-hub" },
  { hub: "pnl",           label: "Trading P&L",           desc: "Ikot Ekpene · Four Lane · Consolidated comparative.",        Icon: Scale,      color: C.green,    tint: C.greenTint, to: "/accounting/statements" },
  { hub: "funds-release", label: "Funds Release",         desc: "Cash inflow released for operations.",                       Icon: Banknote,   color: "#6E28A6",  tint: "#F2E7FD", to: "/accounting/statements" },
];

const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function Reports() {
  const { locationId } = useOutletContext();
  const [busy, setBusy] = useState(null);

  const doExport = async (key, fmt) => {
    setBusy(`${key}-${fmt}`);
    const date = new Date().toISOString().slice(0, 10);
    const params = locationId ? { location_id: locationId } : {};
    await downloadReport(`/reports/exports/${key}.${fmt}`, `${key}-${date}.${fmt}`, params);
    setBusy(null);
  };

  const doExportHub = async (hub, fmt) => {
    setBusy(`${hub}-${fmt}`);
    const period = thisMonth();
    const params = { period };
    if (locationId) params.location_id = locationId;
    await downloadReport(`/reports/hub/exports/${hub}.${fmt}`, `${hub}-${period}.${fmt}`, params);
    setBusy(null);
  };

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div className="rounded-xl bg-white border p-4 flex items-center gap-3" style={{ borderColor: C.border, background: "#FDF6E3" }}>
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#F5EDDC", color: C.gold }}><FileDown size={18} /></div>
        <div className="flex-1">
          <div className="font-brand font-bold" style={{ color: C.text }}>Board-ready exports</div>
          <div className="text-[12.5px]" style={{ color: C.muted }}>Every report exports to CSV (data) and PDF (branded, print-ready) in one click.</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {REPORTS.map((r) => (
          <div key={r.key} className="rounded-xl bg-white border p-5 flex flex-col gap-3 hover:shadow-md transition-shadow" style={{ borderColor: C.border }} data-testid={`report-card-${r.key}`}>
            <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: r.tint, color: r.color }}><r.Icon size={20} /></div>
            <div className="flex-1">
              <div className="font-brand font-bold text-lg" style={{ color: C.text }}>{r.label}</div>
              <div className="text-[12.5px] mt-1" style={{ color: C.muted }}>{r.desc}</div>
            </div>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {r.to && (
                <Link to={r.to} className="h-9 px-3 text-[12px] font-semibold rounded-md border flex items-center gap-1.5" style={{ borderColor: C.border, color: C.text }} data-testid={`report-open-${r.key}`}>Open <ArrowRight size={12} /></Link>
              )}
              {r.csv && (
                <button onClick={() => doExport(r.key, "csv")} disabled={busy === `${r.key}-csv`}
                  className="h-9 px-3 text-[12px] font-semibold rounded-md flex items-center gap-1.5 disabled:opacity-50"
                  style={{ background: C.gold, color: "#FFFFFF" }} data-testid={`export-csv-${r.key}`}>
                  <FileDown size={12} /> {busy === `${r.key}-csv` ? "…" : "CSV"}
                </button>
              )}
              {r.pdf && (
                <button onClick={() => doExport(r.key, "pdf")} disabled={busy === `${r.key}-pdf`}
                  className="h-9 px-3 text-[12px] font-semibold rounded-md flex items-center gap-1.5 disabled:opacity-50 text-white"
                  style={{ background: C.burgundy }} data-testid={`export-pdf-${r.key}`}>
                  <FileText size={12} /> {busy === `${r.key}-pdf` ? "…" : "PDF"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl bg-white border p-4 flex items-center gap-3 mt-6" style={{ borderColor: C.border, background: "#FDF6E3" }}>
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#F5EDDC", color: C.gold }}><FileSpreadsheet size={18} /></div>
        <div className="flex-1">
          <div className="font-brand font-bold" style={{ color: C.text }}>Daily workbook reports</div>
          <div className="text-[12.5px]" style={{ color: C.muted }}>Excel (.xlsx) exports mirror the legacy monthly workbooks — per-day sheets, ₦ formatting & live formulas. Open for the full interactive view.</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {WORKBOOKS.map((r) => (
          <div key={r.hub} className="rounded-xl bg-white border p-5 flex flex-col gap-3 hover:shadow-md transition-shadow" style={{ borderColor: C.border }} data-testid={`workbook-card-${r.hub}`}>
            <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: r.tint, color: r.color }}><r.Icon size={20} /></div>
            <div className="flex-1">
              <div className="font-brand font-bold text-lg" style={{ color: C.text }}>{r.label}</div>
              <div className="text-[12.5px] mt-1" style={{ color: C.muted }}>{r.desc}</div>
            </div>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Link to={r.to} className="h-9 px-3 text-[12px] font-semibold rounded-md border flex items-center gap-1.5" style={{ borderColor: C.border, color: C.text }} data-testid={`workbook-open-${r.hub}`}>Open <ArrowRight size={12} /></Link>
              <button onClick={() => doExportHub(r.hub, "xlsx")} disabled={busy === `${r.hub}-xlsx`}
                className="h-9 px-3 text-[12px] font-semibold rounded-md flex items-center gap-1.5 disabled:opacity-50 border"
                style={{ borderColor: C.gold, color: C.burgundy }} data-testid={`workbook-xlsx-${r.hub}`}>
                <FileSpreadsheet size={12} /> {busy === `${r.hub}-xlsx` ? "…" : "Excel"}
              </button>
              <button onClick={() => doExportHub(r.hub, "pdf")} disabled={busy === `${r.hub}-pdf`}
                className="h-9 px-3 text-[12px] font-semibold rounded-md flex items-center gap-1.5 disabled:opacity-50 text-white"
                style={{ background: C.burgundy }} data-testid={`workbook-pdf-${r.hub}`}>
                <FileText size={12} /> {busy === `${r.hub}-pdf` ? "…" : "PDF"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
